import { useState } from 'react';
import { ArchiveIcon } from 'lucide-react';
import {
  Button,
  Callout,
  DataTable,
  EmptyState,
  Input,
  SimpleTooltip,
  toast,
  type ColumnDef,
} from '@opengoal/ui';
import { useTranslation } from 'react-i18next';
import { formatDate, formatDateTime } from '@/shared/lib/format';
import type { OffboardingDetailDto, RetentionRecordDto } from '@/api/generated/model';
import {
  useArchiveAllMutation,
  useArchiveRetentionMutation,
  useUpdateRetentionMutation,
} from '../hooks/useOffboarding';
import { StepSection } from './StepSection';

export interface RetentionsPanelProps {
  offboarding: OffboardingDetailDto;
  readOnly?: boolean;
}

/**
 * Step 4 — data archiving and retention.
 *
 * Two things the UI must not imply. **No data is moved or deleted** — archiving
 * records the intent and fixes a purge date, and there is no purge job. And
 * **the purge date does not move once set**: the retention period is editable
 * before archiving and inert afterwards, because a schedule that shifts when
 * someone edits a policy is not a schedule. The control is disabled on archived
 * rows rather than silently ignored, so the rule is visible.
 */
export function RetentionsPanel({ offboarding, readOnly = false }: RetentionsPanelProps) {
  const { t } = useTranslation();

  const update = useUpdateRetentionMutation(offboarding.id);
  const archive = useArchiveRetentionMutation(offboarding.id);
  const archiveAll = useArchiveAllMutation(offboarding.id);

  const fail = (error: unknown) =>
    toast.error(error instanceof Error ? error.message : t('offboarding.retentions.failed'));

  const unarchived = offboarding.retentions.filter((row) => !row.archivedAt);

  const columns: ColumnDef<RetentionRecordDto>[] = [
    {
      id: 'dataType',
      header: t('offboarding.retentions.dataType'),
      cell: ({ row }) => row.original.dataType,
    },
    {
      id: 'legalBasis',
      header: t('offboarding.retentions.legalBasis'),
      cell: ({ row }) => (
        <span className="whitespace-normal text-muted-foreground">{row.original.legalBasis}</span>
      ),
    },
    {
      id: 'retentionMonths',
      header: t('offboarding.retentions.retentionMonths'),
      meta: { width: 160 },
      cell: ({ row: { original: row } }) => (
        <SimpleTooltip content={row.archivedAt ? t('offboarding.retentions.lockedHint') : ''}>
          <span className="block">
            <MonthsInput
              value={row.retentionMonths}
              disabled={readOnly || Boolean(row.archivedAt)}
              onCommit={(retentionMonths) =>
                update.mutate({ retentionId: row.id, dto: { retentionMonths } }, { onError: fail })
              }
            />
          </span>
        </SimpleTooltip>
      ),
    },
    {
      id: 'archivedAt',
      header: t('offboarding.retentions.archived'),
      meta: { width: 170 },
      cell: ({ row }) => (row.original.archivedAt ? formatDateTime(row.original.archivedAt) : '—'),
    },
    {
      id: 'scheduledPurgeAt',
      header: t('offboarding.retentions.scheduledPurge'),
      meta: { width: 150 },
      cell: ({ row }) =>
        row.original.scheduledPurgeAt ? formatDate(row.original.scheduledPurgeAt) : '—',
    },
    ...(readOnly
      ? []
      : [
          {
            id: 'archive',
            header: t('offboarding.retentions.action'),
            meta: { width: 120, align: 'end' },
            cell: ({ row: { original: row } }) => (
              <Button
                variant="outline"
                size="xs"
                disabled={Boolean(row.archivedAt)}
                loading={archive.isPending && archive.variables === row.id}
                onClick={() =>
                  archive.mutate(row.id, {
                    onSuccess: () => toast.success(t('offboarding.retentions.archivedOne')),
                    onError: fail,
                  })
                }
              >
                {t('offboarding.retentions.archive')}
              </Button>
            ),
          } satisfies ColumnDef<RetentionRecordDto>,
        ]),
  ];

  return (
    <StepSection
      step={4}
      title={t('offboarding.retentions.title')}
      extra={
        readOnly ? null : (
          <Button
            variant="outline"
            size="sm"
            disabled={unarchived.length === 0}
            loading={archiveAll.isPending}
            onClick={() =>
              archiveAll.mutate(undefined, {
                onSuccess: () => toast.success(t('offboarding.retentions.archivedAll')),
                onError: fail,
              })
            }
          >
            <ArchiveIcon />
            {t('offboarding.retentions.archiveAll')}
          </Button>
        )
      }
    >
      <div className="grid gap-4">
        <Callout
          variant="warning"
          title={t('offboarding.retentions.stubTitle')}
          description={t('offboarding.retentions.stubBody')}
        />

        <DataTable<RetentionRecordDto>
          columns={columns}
          data={offboarding.retentions}
          getRowId={(row) => row.id}
          emptyState={<EmptyState description={t('offboarding.retentions.empty')} />}
        />
      </div>
    </StepSection>
  );
}

/**
 * Whole months, committed on blur or Enter rather than per keystroke — typing
 * "120" would otherwise send 1, then 12, then 120 as three separate writes.
 */
function MonthsInput({
  value,
  disabled,
  onCommit,
}: {
  value: number;
  disabled: boolean;
  onCommit: (value: number) => void;
}) {
  const [text, setText] = useState(String(value));
  // Re-sync after a save returns the stored value; adjusted during render
  // rather than in an effect.
  const [synced, setSynced] = useState(value);
  if (synced !== value) {
    setSynced(value);
    setText(String(value));
  }

  const commit = () => {
    const parsed = Math.round(Number(text));
    if (text.trim() === '' || !Number.isFinite(parsed) || parsed < 0 || parsed > 600) {
      setText(String(value));
      return;
    }
    if (parsed !== value) onCommit(parsed);
  };

  return (
    <Input
      type="number"
      min={0}
      max={600}
      className="h-8"
      value={text}
      disabled={disabled}
      onChange={(event) => setText(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') commit();
      }}
    />
  );
}
